import { API_BASE } from '../config';
import { CloudSync } from './CloudSync';

/** Online Arena (tournaments) client. All calls require a signed-in account. */
const BASE = API_BASE;

export type TournStatus = 'upcoming' | 'running' | 'ending' | 'settled';

const TIME_DISCIPLINES = new Set(['sprint_100m', 'hurdles', 'swim_50m']);
/** Metric kind for a discipline (mirrors the server). */
export const metricKindOf = (id: string): 'time' | 'distance' => (TIME_DISCIPLINES.has(id) ? 'time' : 'distance');

export interface TournView {
  id: string;
  name: string;
  creatorName: string;
  system: boolean;
  disciplines: string[];
  startAt: number;
  endAt: number;
  entryFee: number;
  maxPlayers: number;
  visibility: 'public' | 'private';
  status: TournStatus;
  pool: number;
  entrantCount: number;
  joined: boolean;
  mine: boolean;
  settled: boolean;
  myPayout: number;
}

export interface StandingRow { uid: string; name: string; points: number }
export interface DiscRow { uid: string; name: string; metric: number }
export interface TournDetail extends TournView {
  standings: StandingRow[];
  myResults: Record<string, number>;
  byDiscipline: Record<string, DiscRow[]>;
  code?: string;
}

export interface CreateConfig {
  name: string;
  disciplines: string[];
  startAt?: number;
  endAt: number;
  entryFee: number;
  maxPlayers: number;
  visibility: 'public' | 'private';
}

interface ApiResult<T> { ok: boolean; error?: string; data?: T }

async function post<T>(path: string, extra: Record<string, unknown>): Promise<ApiResult<T>> {
  const auth = CloudSync.authBody();
  if (!auth) return { ok: false, error: 'not_signed_in' };
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...auth, ...extra }),
    });
    const json = (await res.json().catch(() => null)) as (Record<string, unknown> & { ok?: boolean; error?: string }) | null;
    if (!res.ok || !json?.ok) return { ok: false, error: json?.error ?? `http_${res.status}` };
    return { ok: true, data: json as unknown as T };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export function arenaList(): Promise<ApiResult<{ now: number; coins: number | null; tournaments: TournView[] }>> {
  return post('/arena/list', {});
}
export function arenaDetail(tournamentId: string): Promise<ApiResult<{ now: number; coins: number | null; tournament: TournDetail }>> {
  return post('/arena/detail', { tournamentId });
}
export function arenaCreate(cfg: CreateConfig): Promise<ApiResult<{ coins: number | null; tournament: TournDetail }>> {
  return post('/arena/create', cfg as unknown as Record<string, unknown>);
}
export function arenaJoin(tournamentId: string, code?: string): Promise<ApiResult<{ coins: number | null; tournament: TournDetail }>> {
  return post('/arena/join', { tournamentId, code });
}
export function arenaResult(tournamentId: string, disciplineId: string, metric: number): Promise<ApiResult<{ tournament: TournDetail }>> {
  return post('/arena/result', { tournamentId, disciplineId, metric });
}
export function arenaUpdate(tournamentId: string, patch: Partial<CreateConfig>): Promise<ApiResult<{ coins: number | null; tournament: TournDetail }>> {
  return post('/arena/update', { tournamentId, ...patch });
}
export function arenaCancel(tournamentId: string): Promise<ApiResult<{ coins: number | null }>> {
  return post('/arena/cancel', { tournamentId });
}

/** Compact "time remaining" string (e.g. "2g 4h", "5h 12m", "8m"). */
export function formatTimeLeft(ms: number): string {
  if (ms <= 0) return '—';
  const m = Math.floor(ms / 60_000), h = Math.floor(m / 60), d = Math.floor(h / 24);
  if (d >= 1) return `${d}g ${h % 24}h`;
  if (h >= 1) return `${h}h ${m % 60}m`;
  return `${Math.max(1, m)}m`;
}

/** Prize split (fractions) by entrant count — mirrors the server for client-side display. */
export function prizeShares(n: number): number[] {
  if (n <= 1) return [1];
  if (n < 5) return [1];
  if (n < 10) return [0.7, 0.3];
  return [0.6, 0.25, 0.15];
}
